<template>
  <div :id=id class="uv-wrapper" />
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, watch } from 'vue'
import loadUniversalViewer from '@/utils/universal-viewer/loader'
import { useRoute } from 'vue-router'

const emit = defineEmits(['init', 'prev', 'next', 'close'])

const route = useRoute()
const id = ref(`uv-${Math.random().toString(36).slice(2, 12).padEnd(10, '0')}`)
const uv = ref(null)
const isPreviewFileInArchive = ref(false)
const getUVIframeDocument = () => {
  const iframe = document.querySelector('iframe.syno-uv-main')
  return iframe?.contentDocument
}

const onKeyDown = (e) => {
  if (e.key === 'ArrowUp') {
    emit('prev')
    e.preventDefault()
  } else if (e.key === 'ArrowDown') {
    emit('next')
    e.preventDefault()
  } else if (e.key === 'Escape') {
    // uv will handle this event directly, so we don't need to hide or leave again.
    if (isPreviewFileInArchive.value) {
      isPreviewFileInArchive.value = false
    } else {
      emit('hide')
    }
    e.preventDefault()
  }
}

const onChange = (index) => {
  isPreviewFileInArchive.value = false
  emit('viewFile', index)
}

const onChangeInArchive = () => {
  isPreviewFileInArchive.value = true
}

const showNext = () => {
  uv.value.showNextSelector()
}

const showPrev = () => {
  uv.value.showPrevSelector()
}

const show = (index, items) => {
  uv.value.hide()
  uv.value.show({
    index,
    items,
    enableLoop: true,
  })
}

const leaveArchive = () => {
  uv.value.leaveCurrentSelector()
  isPreviewFileInArchive.value = false
}

const close = () => {
  if (isPreviewFileInArchive.value) {
    leaveArchive()
  } else {
    uv.value.hide()
    emit('hide')
  }
}

onMounted(async () => {
  uv.value = await loadUniversalViewer(document.getElementById(id.value))

  getUVIframeDocument()?.documentElement.addEventListener('keydown', onKeyDown)
  uv.value.on('change', onChange)
  uv.value.on('changeInArchive', onChangeInArchive)
})

onBeforeUnmount(() => {
  getUVIframeDocument()?.documentElement.removeEventListener('keydown', onKeyDown)
  uv.value.off('change', onChange)
  uv.value.off('changeInArchive', onChangeInArchive)
})

watch(route, () => {
  emit('close')
})

defineExpose({
  showNext,
  showPrev,
  show,
  close,
  leaveArchive,
})
</script>

<style lang="scss" scoped>
.uv-enter, .uv-leave-to {
  opacity: 0;
}

.uv-enter-active {
  transition: opacity .16s ease-out;
}

.uv-wrapper {
  position: absolute;
  width: 100%;
  height: 100%;

  :deep(.syno-uv-main) {
    width: 100%;
    height: 100%;
    border: none;
  }
}
</style>
